import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { EpisodeGrid, LearningPathCardGrid } from '@/containers';
import { EVENT_CONSTANTS } from '@/services';
import {
  LearningPathsUtils,
  playerSessionsSelectors,
  selectPlan,
  selectUserAudienceRegions,
  selectUserCompliance,
  useFetchCatalogSearchResultsQuery,
} from '@/store';
import { appStyles } from '@/theme';

export const Search = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const { searchTerm } = Object.fromEntries(params.entries());

  const audienceRegions = useSelector(selectUserAudienceRegions);
  const compliance = useSelector(selectUserCompliance);
  const playerSessions = useSelector(playerSessionsSelectors.selectEntities);
  const plan = useSelector(selectPlan);

  const {
    data: searchResults,
    isLoading,
    isFetching,
  } = useFetchCatalogSearchResultsQuery({
    searchTerm,
    compliance,
    audienceRegions,
    organizationName: plan?.data?.organizationName,
    referrer: { page: EVENT_CONSTANTS.PAGE.SEARCH, component: 'global_search' },
  });

  const learningPathsWithDuration = useMemo(() => {
    if (!searchResults?.learningPaths) return [];
    return LearningPathsUtils.calculateProgress({
      lps: searchResults.learningPaths,
      playerSessions,
    });
  }, [searchResults?.learningPaths, playerSessions]);

  const episodes = searchResults?.episodes || [];
  const isEmpty =
    learningPathsWithDuration?.length === 0 && episodes?.length === 0;

  return (
    <Box sx={appStyles.limitPageWidth}>
      {!isFetching && (
        <Typography variant="h3">
          {episodes.length + learningPathsWithDuration.length} Search Results
          for &quot;{searchTerm}&quot;
        </Typography>
      )}
      {isFetching ? (
        <EpisodeGrid isLoading={true} />
      ) : isEmpty ? (
        <Box sx={{ minHeight: '25vh' }}>
          <Typography
            sx={{
              mt: 2,
            }}>{`No search results for '${searchTerm}'`}</Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 4 }}>
          {learningPathsWithDuration.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ fontVariantLigatures: 'none' }}>
                Learning Paths ({learningPathsWithDuration.length})
              </Typography>
              <LearningPathCardGrid
                lps={learningPathsWithDuration}
                isLoading={isLoading || isFetching}
                referrer={{ page: EVENT_CONSTANTS.PAGE.SEARCH }}
              />
            </Box>
          )}
          {episodes.length > 0 && (
            <Box>
              <Typography
                variant="h4"
                sx={{ fontVariantLigatures: 'none', mb: 4 }}>
                Episodes ({episodes.length})
              </Typography>
              <EpisodeGrid
                episodes={episodes}
                isLoading={isLoading || isFetching}
                referrer={{ page: EVENT_CONSTANTS.PAGE.SEARCH }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
